import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Button, FormControl, Popover} from "react-bootstrap";
import {Glyphicon} from "../../Common/GlyphIcon";
import {SB_ENTER_KEY_CODE} from "../../../Constants";
import {GuidUtils, ObjectUtils} from "../../../Utils";
import CheckboxTree from "react-checkbox-tree";
import {KeyboardUtils} from "../../../Utils";
import {useDebouncedEffect} from "../../../Hooks/EffectHooks";
import {
    useDeleteColumns,
    useFindColumnById,
    useGetPlaceholderGuid,
    useInsertColumns,
    useUpdateColumn
} from "../../../Hooks/QueryToolHooks";
import {useDispatch, useSelector} from "react-redux";
import {SET_CHECKED, SET_EXPANDED} from "../../../Redux/Actions";

export const AddColumnPopover = React.forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const getPlaceholderGuid = useGetPlaceholderGuid();
    const findColumnById = useFindColumnById();
    const insertColumns = useInsertColumns();
    const deleteColumns = useDeleteColumns();

    const {checked, expanded, allColumns, newColumns} = useSelector(state => state.queryToolState);

    const saveButton = useRef(null);

    const [nodes, setNodes] = useState([]);
    const [nodesFiltered, setNodesFiltered] = useState([]);
    const [filterText, setFilterText] = useState('');

    const guid = getPlaceholderGuid;

    useEffect(() => {
        const newNodes = convertData(listToTree(allColumns));
        setNodes(newNodes);
        setNodesFiltered(newNodes);

        const handleKeyDown = (e) => {
            KeyboardUtils.handleKeyPress(SB_ENTER_KEY_CODE, e, () => {
                saveButton.current.focus();
                saveButton.current.click();
            });
        }

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }
    }, [allColumns])

    useDebouncedEffect(() => {
        filterTree()
    }, [filterText], 250)


    const listToTree = (list) => {
        const treeList = [];
        const lookup = {};

        list.forEach((obj) => {
            lookup[obj.name] = obj;
            obj.children = [];
        });

        list.forEach((currentNode) => {

            let parentNode = lookup[currentNode.parentColumnSourceGroupName];

            currentNode.description = currentNode.name;
            if(parentNode) {
                currentNode.description = currentNode.description.replace(parentNode.name + '.', '');
            }

            // add this currentNode to the tree
            if(!parentNode || currentNode.isTopLevel) {
                // or at the topLevel if we don't
                treeList.push(currentNode);
            } else if (currentNode.ColumnSource.find(x => !x.sbShowInParent)) {
                // under a parent if we have one
				// and make sure it has columns that are not collapsed to parent
                const columnSourceGroups = parentNode.children.filter(x => x.parentColumnSourceGroupId !== undefined);
                const rest = parentNode.children.filter(x => x.parentColumnSourceGroupId === undefined);

                // append and sort
                const newSourceGroups = [...columnSourceGroups, currentNode]
                    .sort((a, b) => {
                        return a?.title?.localeCompare(b?.title, 'en', {numeric: true});
                    });

                parentNode.children = [...newSourceGroups, ...rest];
            }

            // always include column sources as children of their direct parent node
            currentNode.ColumnSource.forEach((x) => {
                if (x.sbShowInParent) {
                    parentNode.children.unshift({
                        ...x,
                        shortName: x.sbShowInParent,
                        shortTitle: x.shortTitle ?? x.sbShowInParent,
                        title: x.title ?? x.sbShowInParent,
                        description: parentNode.description + '.' + currentNode.description + '.' + x.shortName,
                        className: (x?.groupingMethodId === null) ? 'sb-key-column-source ' : '',
                    });
                } else {
                    currentNode.children.push({
                        ...x,
                        description: currentNode.description + '.' + x.shortName,
                        className: (x?.groupingMethodId === null) ? 'sb-key-column-source ' : '',
                    });
                }
            });
        });

        return treeList;
    };

    const convertData = (data) => {
        return data.map((x) => ({
            ...x,
            value: x.name || `group-${x.columnSourceGroupName}`,
            label: <Fragment>
                <span key={1} className="tree-title">{x.shortTitle || x.title}</span>{" "}
                <span key={2} className="tree-description">{x.description}</span>
                { x?.groupingMethodId === null ? <img key={0} className="group" src="/img/group.svg" width={12} height={12} /> : null }
                { x?.filters?.length ? <img key={3} className="filter" src="/img/filter.svg" width={12} height={12} /> : null }
            </Fragment>,
            searchValue: `${x.description}|${x.shortTitle}|${x.title}`,
            children: x.children && convertData(x.children),
            title: x.title + "\n" + x.description
        }));
    }

    const onFilterChange = (e) => {
        const filterText = e.target.value;

        setFilterText(filterText);

        if (filterText === '') {
            // clear expanded nodes
            onExpand([]);
        }
    }

    const filterTree = () => {
        let nodesFiltered = nodes; // Resets nodes back to unfiltered state
        let expandedByFilter = [];

        if (filterText) {
            // split by space, but filter array to only include non-empty values
            const searchTerms = filterText.toLocaleLowerCase().split(' ').filter(text => text);
            nodesFiltered = ObjectUtils.DeepClone(nodes).map(node => addFilterClass(node, searchTerms));
            expandedByFilter = nodesFiltered.reduce(expandedByFilterReducer, []);
        }

        setNodesFiltered(nodesFiltered)
        onExpand([...expanded, ...expandedByFilter]);
    }

    const filterNodes = (node, searchTerms) => {
        if(searchTerms.length === 0) {
            return true;
        }

        const searchValue = node.searchValue.toLocaleLowerCase();

        // make sure every word is included in search value
        const unmatchedSearchTerms = searchTerms.filter(text => !searchValue.includes(text));

        if (node.children) {
            node.children = ([...node.children] || []).map(node => addFilterClass(node, unmatchedSearchTerms))
                .sort((a, b) => {
                    // put associations at bottom when searching
                    const aHasParent = Boolean(a.parentColumnSourceGroupName);
                    const bHasParent = Boolean(b.parentColumnSourceGroupName);

                    if (aHasParent === bHasParent) {
                        return a?.title?.localeCompare(b?.title, 'en', {numeric: true});
                    }

                    return aHasParent ? 1 : -1;
                });
        }

        // short circuit out of further processing
        // if we know the parent includes the text
        if(unmatchedSearchTerms.length === 0) {
            return true;
        }

        // otherwise check to see if any children match
        if(node?.children?.filter(node => filterNodes(node, unmatchedSearchTerms)).length) {
            return true;
        }

        // if no matches hide
        return false;
    }

    const addFilterClass = (node, searchTerms) => {
        if (!filterNodes(node, searchTerms)) {
            return {
                ...node,
                className: (node?.className ?? '') + ' sb-hidden-column-source'
            }
        }

        return {
            ...node
        }
    }

    const expandedByFilterReducer = (accumulator, node) => {
        if(node.children && node.children.length) {
            return node.children.reduce(expandedByFilterReducer, [...accumulator, node.value]);
        }
        return accumulator;
    }

    const onCheck = (guid, checkedArray) => {
        const added = checkedArray
            .filter(id => !checked.includes(id))
            .map(id => findColumnById(id));
        const deleted = checked
            .filter(id => !checkedArray.includes(id))
            .map(id => {
                const column = newColumns.find(x => x?.data?.name == id);

                return column?.data?.guid;
            });
        const shouldResetPlaceholder = deleted.length === newColumns.length;

        if (added.length > 0) insertColumns(guid, added);
        if (deleted.length > 0) deleteColumns(deleted, shouldResetPlaceholder);

        dispatch({type: SET_CHECKED, checked: checkedArray});
    }

    const onExpand = (expanded, targetNode) => {
        // we need a special handler since we mix in the filtered parents
        // and we can't rely on the expanded state passed to us from the
        // CheckboxTree component

        // let expanded = props.expanded.filter( x => x !== targetNode.value );
        // if(unreliableExpanded.includes(targetNode.value)) {
        //     expanded.push(targetNode.value);
        // }
        // return props.onExpand?.(expanded, targetNode);

        dispatch({type: SET_EXPANDED, expanded})
    }

    return (
        <Popover
            ref={ref}
            id={"sb-query-column-add-popup-" + guid}
            placement="bottom"
        >
            <div className="popover-title shadow-bottom">
                <label className="sb-query-input-labels">Column Source</label>
                <FormControl
                    type="text"
                    value={filterText}
                    onChange={onFilterChange}
                    placeholder="Type to search"
                    autoFocus
                />
            </div>
            <div className="popover-content">
                <CheckboxTree
                    nodes={nodesFiltered}
                    checked={checked}
                    expanded={expanded}
                    onCheck={checked => onCheck(guid, checked)}
                    onExpand={onExpand}
                    showNodeIcon={false}
                    icons={{
                        check: <img src="/img/box-checked.svg" />,
                        uncheck: <img src="/img/box-unchecked.svg" />,
                        halfCheck: <img src="/img/box-altchecked.svg" />,
                        expandClose: <img src="/img/right-caret.svg" width={12} height={12} />,
                        expandOpen: <img src="/img/down-caret.svg" width={12} height={12} />,
                        expandAll: null,
                        collapseAll: null,
                        parentClose: null,
                        parentOpen: null,
                        leaf: null,
                    }}
                />
            </div>
            <div className="popover-footer btn-edit shadow-top justify-right">
                <Button variant="cancel" onClick={props.cancelAdd}>Cancel</Button>
                <Button
                    variant="save"
                    onClick={() => props.saveNewColumns(guid)}
                    ref={saveButton}
                >
                    {checked.length > 1 ? 'Add Columns' : 'Add Column'}
                </Button>
            </div>
        </Popover>
    );
})

export const EditColumnPopover = React.forwardRef((props, ref) => {
    const updateColumn = useUpdateColumn();
    const deleteColumns = useDeleteColumns();

    const {filterMethods, useGroupBy, groupingMethods} = useSelector(state => state.queryToolState);

    const {data} = props;

    const saveButton = useRef(null);

    const [filters, setFilters] = useState(data.filters ? [...data.filters] : []);
    const [title, setTitle] = useState(data.title ? data.title : '');

    useEffect(() => {
        const handleKeyDown = (e) => {
            KeyboardUtils.handleKeyPress(SB_ENTER_KEY_CODE, e, () => {
                    saveButton.current.focus();
                    saveButton.current.click();
                }
            );
        }

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }
    }, [])


    const addFilter = () => {
        const defaultFilter = filterMethods.find(x => x.code === 'equals');
        const newFilter = {
            ...defaultFilter,
            guid: GuidUtils.Generate(),
            value: '',
        };

        setFilters([...filters, newFilter])
    }

    const updateFilter = (guid, value, field, updateParent = false) => {
        const alteredFilters = [...filters].map(filter => {
            if (filter.guid === guid) {
                let alteredData = {};

                switch (field) {
                    case 'id':
                        const changedFilter = filterMethods.find(x => x.filterMethodId === Number(value));
                        alteredData = {id: value, ...changedFilter};
                        break;
                    case 'value':
                        alteredData = {value};
                        break;
                }

                return {...filter, ...alteredData}
            }

            return filter;
        });

        setFilters(alteredFilters);

        if (updateParent) {
            updateColumnData(alteredFilters, 'filters', true);
        }
    }

    const updateColumnData = (e, field, isStale = false) => {
        const updatedData = {...data, [field]: e};

        updateColumn(data.guid, updatedData, 'columns', isStale)
    }

    const deleteFilter = (guid) => {
        const filtersClone = [...filters];
        const index = filtersClone.findIndex((x) => guid === x.guid);

        if (index !== -1) filtersClone.splice(index, 1);

        setFilters(filtersClone);
        updateColumnData(filtersClone, 'filters', true);
    }

    const renderGroupingMethods = () => {
        const value = data.aggregate != null ? data.aggregate : data.groupingMethodId || '';

        return (
            <FormControl
                as="select"
                value={value}
                onChange={(e) => updateColumnData(e.target.value, 'aggregate', true)}
            >
                <option value={''}>Group by This Column {data.groupingMethodId === null ? '(Default)' : ''}</option>
                {groupingMethods.map(x => (
                    <option key={x.groupingMethodId} value={x.groupingMethodId}>
                        {x.name} {data.groupingMethodId === x.groupingMethodId ? '(Default)' : ''}
                    </option>
                ))}
            </FormControl>
        );
    }

    const renderFilterColumns = () => {
        if (!filters) return null;

        return filters.map(filter => (
            <div key={filter.guid} className="filter-column">
                <FormControl
                    as="select"
                    value={filter.filterMethodId}
                    onChange={e => updateFilter(filter.guid, e.target.value, 'id', true)}
                >
                    {filterMethods.map(x => (
                        <option key={x.filterMethodId} value={x.filterMethodId}>{x.name}</option>
                    ))}
                </FormControl>
                {
                    filter.hasValue ?
                    <FormControl
                        type="text"
                        value={filter.value}
                        placeholder={filter.placeholderText}
                        onChange={e => updateFilter(filter.guid, e.target.value, 'value')}
                        onBlur={e => updateColumnData(filters, 'filters', true)}
                        autoFocus
                    /> : null
                }
                <div className="remove-table">
                    <a className="pndlm-remove-control" onClick={() => deleteFilter(filter.guid)}>
                        <Glyphicon glyph="remove-sign" />
                    </a>
                </div>
            </div>
        ))
    }

    return (
        <Popover
            ref={ref}
            id={"sb-query-column-editor-popup-" + data.guid}
            placement="bottom"
            style={{left: 0, top: '70px', minWidth: '100%'}}
        >
            <div className="popover-title p-0">
                <div className="filter-title" onClick={() => addFilter()}>
                    <div>
                        <Glyphicon glyph="filter" />
                        <span>Add a Filter</span>
                    </div>
                    <div className="add-table">
                        <a className="pndlm-remove-control">
                            <Glyphicon glyph="plus-sign" />
                        </a>
                    </div>
                </div>
            </div>
            <div className="popover-content">
                {renderFilterColumns()}
                <div className="popover-content-block">
                    <label className="sb-query-input-labels">Column Title</label>
                    <FormControl
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onBlur={(e) => updateColumnData(title, 'title')}
                    />
                </div>
                {
                    useGroupBy ?
                    <div className="popover-content-block">
                        <label className="sb-query-input-labels">Grouping Method</label>
                        {renderGroupingMethods()}
                    </div> : null
                }
            </div>
            <div className="popover-footer btn-edit justify-center">
                {/* Don't refetch data if useGroupBy is checked or if query is grouped by this column */}
                <Button variant="delete" onClick={() => deleteColumns([data.guid], false, useGroupBy && data.groupingMethodId === null)}>Delete Column</Button>
                <Button variant="cancel" onClick={() => props.cancelEdit()}>Cancel</Button>
                <Button
                    variant="save"
                    onClick={() => props.saveColumn()}
                    ref={saveButton}
                >
                    Save
                </Button>
            </div>
        </Popover>
    );
})
